import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {PdfThumbnailService} from './pdf-thumbnail.service';
import {defer, EMPTY, forkJoin, from, mergeMap, Observable, of, Subject, throwError, timer} from "rxjs";
import {catchError, exhaustMap, filter, finalize, map, switchMap, take, tap, timeout} from "rxjs/operators";
import {FileRestService} from "../../api/services/file.rest.service";
import {DocumentApiModel} from "../../api/models/document.api-model";
import {DocumentRestService} from "../../api/services/document.rest.service";
import {ProjectApiModel} from "../../api/models/project.api-model";

export class DocumentReadError extends Error {
  constructor(public readonly code: 'DOCUMENT_PROCESSING_FAILED' | 'DOCUMENT_PROCESSING_TIMEOUT' | 'DOCUMENT_PREVIEW_FAILED') {
    super(code);
  }
}

@Injectable({
  providedIn: 'root',
})
export class DocumentUploadService {

  public files: File[] = [];
  public newFiles: File[] = [];
  constructor(
    private readonly documentRS: DocumentRestService,
    private readonly frs: FileRestService,
    private readonly pdfThumbnail: PdfThumbnailService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  uploadDocument(documentsAMs: DocumentApiModel[]): Observable<{ documents$: Observable<DocumentApiModel[]>; progress$: Observable<number> }> {
    const progress$ = new Subject<number>();
    const uploadRequests: Observable<DocumentApiModel>[] = [];
    const totalFiles = documentsAMs.length;

    const individualProgress: number[] = Array(totalFiles).fill(0);

    documentsAMs.forEach((documentAM, index) => {
      let uploadRequest: Observable<number | DocumentApiModel>;

      if (!documentAM['@id']) {
        uploadRequest = this.documentRS.uploadItemWithProgress(documentAM.type, documentAM.file, documentAM.description);
      } else {
        uploadRequest = this.documentRS.put(documentAM);
      }

      const filteredRequest = uploadRequest.pipe(
        tap(progressOrModel => {
          if (typeof progressOrModel === 'number') {
            individualProgress[index] = progressOrModel;
            progress$.next(individualProgress.reduce((sum, current) => sum + current, 0) / totalFiles);
          }
        }),
        filter((value): value is DocumentApiModel => typeof value !== 'number'),
        map(response => {
          // Retain acknowledged IDs even if another upload or a later stage fails.
          const file = documentAM.file;
          const thumbnail = documentAM.thumbnail;
          Object.assign(documentAM, response, {file, thumbnail});
          return documentAM;
        })
      );

      uploadRequests.push(filteredRequest);
    });

    if (!uploadRequests.length) {
      progress$.next(100); // Jeśli brak plików, zakończ progress
      progress$.complete();
      return of({ documents$: of([]), progress$: of(100) });
    }

    const documents$ = forkJoin(uploadRequests).pipe(
      tap(() => {
        progress$.next(100); // Postęp końcowy
      }),
      finalize(() => progress$.complete())
    );

    return of({ documents$: documents$, progress$: progress$.asObservable() });
  }

  public waitUntilReady(document: DocumentApiModel, forceRead = false): Observable<DocumentApiModel> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(document);
    }
    return defer(() => {
      if (document.processingStatus === 'failed') {
        return throwError(() => new DocumentReadError('DOCUMENT_PROCESSING_FAILED'));
      }
      if (document.processingStatus === 'completed' && !forceRead) {
        return of(document);
      }
      if (!document['@id']) {
        return throwError(() => new DocumentReadError('DOCUMENT_PREVIEW_FAILED'));
      }
      return timer(0, 1000).pipe(
        exhaustMap(() => this.documentRS.get(document['@id'], {headers: new HttpHeaders({'x-cache-bypass': '1'})})),
        mergeMap(fresh => {
          if (fresh.processingStatus === 'failed') {
            return throwError(() => new DocumentReadError('DOCUMENT_PROCESSING_FAILED'));
          }
          return fresh.processingStatus === 'completed'
            ? of(Object.assign(new DocumentApiModel(), document, fresh, {file: document.file, thumbnail: document.thumbnail}))
            : EMPTY;
        }),
        take(1),
        timeout({first: 120000, with: () => throwError(() => new DocumentReadError('DOCUMENT_PROCESSING_TIMEOUT'))})
      );
    });
  }

  public loadReadyDocument(document: DocumentApiModel, width = 150): Observable<DocumentApiModel> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(document);
    }
    const load = (current: DocumentApiModel, forceRead = false): Observable<DocumentApiModel> =>
      this.waitUntilReady(current, forceRead).pipe(switchMap(ready => {
        const url = ready.hyperlinks?.find(link => link.name === 'original')?.uri;
        const localPdf = ready.file?.type === 'application/pdf' ? ready.file : null;
        if (!localPdf && !url) {
          return throwError(() => new DocumentReadError('DOCUMENT_PREVIEW_FAILED'));
        }
        const file$ = localPdf ? of(localPdf) : this.frs.getFile(url, {
          rejectPending: true, headers: new HttpHeaders({'x-cache-bypass': '1'})
        });
        return file$.pipe(
          switchMap(file => {
            if (file.type !== 'application/pdf') {
              return throwError(() => new DocumentReadError('DOCUMENT_PREVIEW_FAILED'));
            }
            const cached = ready.thumbnail || (file as File & {thumbnail?: string}).thumbnail;
            const preview$ = cached ? of(cached) : defer(() => from(this.pdfThumbnail.generateThumbnail(file, width)));
            return preview$.pipe(map(thumbnail => {
              (file as File & {thumbnail?: string}).thumbnail = thumbnail;
              return Object.assign(new DocumentApiModel(), ready, {file, thumbnail, previewErrorKey: undefined});
            }));
          }),
          catchError(error => error instanceof HttpErrorResponse && error.status === 202
            ? timer(1000).pipe(switchMap(() => load(ready, true)))
            : throwError(() => error))
        );
      }));
    // One deadline includes status polling, a late 202, download and preview generation.
    return defer(() => load(document)).pipe(
      timeout({first: 120000, with: () => throwError(() => new DocumentReadError('DOCUMENT_PROCESSING_TIMEOUT'))})
    );
  }

  public previewErrorKey(error: unknown): string {
    const code = error instanceof DocumentReadError ? error.code
      : error instanceof HttpErrorResponse ? error.error?.code : undefined;
    switch (code) {
      case 'DOCUMENT_PROCESSING_FAILED': return 'shared.documents.processing_failed';
      case 'DOCUMENT_PROCESSING_TIMEOUT': return 'shared.documents.processing_timeout';
      default: return 'shared.documents.preview_failed';
    }
  }

  public deleteDocumentForProject(documentId: string, projectAM: ProjectApiModel): Observable<object> {
    return this.documentRS.delete(documentId);
  }

  public convertUrlToFile(fileUrls: string[]): Observable<File[]> {
    console.log(fileUrls);
    const requests: Observable<File>[] = [];
    for (const url of fileUrls) {
      // arr.push(new File([await (await fetch(url)).blob()], url, {type: 'image/jpeg'}));
      requests.push(this.frs.getFile(url).pipe(tap(file => {
        if (!this.files.find(f => f.name == file.name)) {
          this.files.push(file);
        }
      })));
    }

    if (!requests.length) {
      this.files = [...this.newFiles];
      return of(this.files);
    }

    return forkJoin(requests);
  }
}


